import { useState, useRef, MouseEvent } from 'react';
import {
  BackgroundImage,
  CloseButton,
  Controls,
  ScrollContainer,
  StyledImg,
  ZoomButton,
  ZoomedImage,
} from './Styled';
import { Overlay } from 'ui/overlay';
import { zIndex } from 'ui/theme';
import { RangeSlider } from 'ui/rangeSlider/RangeSlider';
import { BUTTON_VARIANTS } from 'ui/button';
import { Close24 } from 'ui/icons/action/Close24';

const MIN_ZOOM = 1;
const DEFAULT_ZOOM = 1.5;
const MAX_ZOOM = 3;
const ZOOM_INTERVAL = 0.5;

export const ImageZoomOverlay = ({
  thumbnail,
  imageSrc,
}: { thumbnail: string; imageSrc: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [isDragging, setIsDragging] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const dragStart = useRef({ x: 0, y: 0 });
  const scrollStart = useRef({ left: 0, top: 0 });

  const handleOpen = () => {
    setZoom(DEFAULT_ZOOM);
    setIsOpen(true);
    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollLeft = 0;
        scrollRef.current.scrollTop = 0;
      }
    }, 0);
  };

  const handleClose = () => setIsOpen(false);

  const zoomIn = () => setZoom((prev) => Math.min(prev + ZOOM_INTERVAL, MAX_ZOOM));
  const zoomOut = () => setZoom((prev) => Math.max(prev - ZOOM_INTERVAL, MIN_ZOOM));

  // 🖱 Mouse drag-to-scroll
  const handleMouseDown = (e: MouseEvent) => {
    if (!scrollRef.current) return;
    setIsDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY };
    scrollStart.current = {
      left: scrollRef.current.scrollLeft,
      top: scrollRef.current.scrollTop,
    };
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    scrollRef.current.scrollLeft = scrollStart.current.left - dx;
    scrollRef.current.scrollTop = scrollStart.current.top - dy;
  };

  const handleNotDragging = () => setIsDragging(false);

  return (
    <>
      <StyledImg src={thumbnail} loading="lazy" onClick={handleOpen} />

      {isOpen && (
        <Overlay isActive zIndex={zIndex.drawerOverlay} preventBodyScroll>
          <BackgroundImage>
            <CloseButton onClick={handleClose} variant={BUTTON_VARIANTS.GHOST}>
              <Close24 />
            </CloseButton>
            <ScrollContainer
              ref={scrollRef}
              isDragging={isDragging}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleNotDragging}
              onMouseLeave={handleNotDragging}
            >
              <ZoomedImage src={imageSrc} zoom={zoom} data-test="zoomed-image" />
            </ScrollContainer>
            <Controls>
              <ZoomButton variant={BUTTON_VARIANTS.TEXT} onClick={zoomOut}>
                -
              </ZoomButton>
              <RangeSlider
                value={[((zoom - MIN_ZOOM) / (MAX_ZOOM - MIN_ZOOM)) * 100]}
                hasTooltip={false}
                onValueChange={(value) =>
                  setZoom(MIN_ZOOM + (value[0] / 100) * (MAX_ZOOM - MIN_ZOOM))
                }
              />
              <ZoomButton variant={BUTTON_VARIANTS.TEXT} onClick={zoomIn}>
                +
              </ZoomButton>
            </Controls>
          </BackgroundImage>
        </Overlay>
      )}
    </>
  );
};
